import Section, { SectionVisibilityProps } from './Section';
import { useTranslation } from 'react-i18next';
import useOrderFlow from '../../../hooks/order/useOrderFlow';
import CheckmarkTextInput from '../../inputs/CheckmarkTextInput';
import collect from 'collect.js';
import PhoneNumberInput from '../../PhoneNumberInput';
import { SectionType } from './CheckoutInfoBag';
import useAppMethods from '../../../hooks/utility/useAppMethods';
import useInitialData from '../../../hooks/global/useInitialData';
import { formatPhoneNumberIntl, isValidPhoneNumber } from 'react-phone-number-input';
import { useMemo, useState } from 'react';
import { User } from 'lucide-react';
import useScreenType from '../../../hooks/utility/useScreenType';

export interface FieldProps {
  value: string;
  onChange: (value: string) => void;
  validationErrors?: string[];
  className?: string;
}

const EMAIL_REGEX: RegExp =
  /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

const isFieldValid = (value: string, extraChecks?: ((value: string) => boolean)[]) =>
  value?.length > 0 && collect(extraChecks).every((callback) => callback(value));

export const FirstNameField = ({ value, onChange, className, ...props }: FieldProps) => {
  const { t } = useTranslation(undefined, { keyPrefix: 'Components:Checkout:Sections:CustomerInfoSection' });

  return (
    <CheckmarkTextInput
      type="text"
      value={value}
      onChange={onChange}
      isComplete={isFieldValid(value)}
      placeholder={t('form.customer_first_name.placeholder')}
      classes={{
        container: className,
      }}
      {...props}
    />
  );
};

export const LastNameField = ({ value, onChange, className, ...props }: FieldProps) => {
  const { t } = useTranslation(undefined, { keyPrefix: 'Components:Checkout:Sections:CustomerInfoSection' });

  return (
    <CheckmarkTextInput
      type="text"
      value={value}
      onChange={onChange}
      isComplete={isFieldValid(value)}
      placeholder={t('form.customer_last_name.placeholder')}
      classes={{
        container: className,
      }}
      {...props}
    />
  );
};

export const EmailField = ({ value, onChange, className, ...props }: FieldProps) => {
  const { t } = useTranslation(undefined, { keyPrefix: 'Components:Checkout:Sections:CustomerInfoSection' });

  return (
    <CheckmarkTextInput
      type="email"
      value={value}
      onChange={onChange}
      isComplete={isFieldValid(value, [(value: string) => EMAIL_REGEX.test(value.toLocaleLowerCase())])}
      placeholder={t('form.customer_email.placeholder')}
      classes={{
        container: className,
      }}
      {...props}
    />
  );
};

export default function CustomerInfoSection({ validationErrors: generalValidationErrors }: SectionVisibilityProps) {
  const { t } = useTranslation(undefined, { keyPrefix: 'Components:Checkout:Sections:CustomerInfoSection' });
  const { t: tOrderInfo } = useTranslation(undefined, { keyPrefix: 'Components:Checkout:OrderInfo' });

  const { localData, update, validationErrors: orderValidationsErrors } = useOrderFlow();
  const validationErrors = generalValidationErrors ?? orderValidationsErrors;
  const { flattenValidationErrors } = useAppMethods();
  const { data: initialData } = useInitialData();

  const { isMobile } = useScreenType();

  const isAllValid = useMemo(() => {
    const firstNameValid = Boolean(localData.customer_first_name);
    const lastNameValid = Boolean(localData.customer_last_name);
    const emailValid =
      Boolean(localData.customer_email) && EMAIL_REGEX.test(localData.customer_email.toLocaleLowerCase());
    const phoneValid = Boolean(localData.customer_phone_number) && isValidPhoneNumber(localData.customer_phone_number);

    return firstNameValid && lastNameValid && emailValid && phoneValid;
  }, [
    localData.customer_first_name,
    localData.customer_last_name,
    localData.customer_email,
    localData.customer_phone_number,
  ]);

  const [initialIsValid] = useState(isAllValid);
  const [isEditing, setIsEditing] = useState(false);

  const showReadonly = initialIsValid && !isEditing;

  const fullName = collect([localData.customer_first_name, localData.customer_last_name])
    .filter((item) => Boolean(item))
    .implode(' ');

  return (
    <Section show={true} id={SectionType.customer_info} title={t('title')}>
      <div className={showReadonly ? 'rounded-xl border-xs border-mobile-secondary bg-brand-card p-md' : ''}>
        {showReadonly ? (
          <div className="flex gap-sm">
            <span className="h-8 w-8 shrink-0 rounded-full bg-mobile-notice flex items-center justify-center">
              <User className="w-4 h-4 text-muted-foreground" />
            </span>
            <div className="mp-mask flex flex-wrap items-center gap-x-xs gap-y-2xs text-sm min-w-0 flex-1">
              {isMobile ? (
                <>
                  <span className="font-semibold text-brand-text-primary w-full">{fullName}</span>
                  <span className="text-muted-foreground truncate w-full">{localData.customer_email}</span>
                  <span className="text-muted-foregroun w-fulld">
                    {formatPhoneNumberIntl(localData.customer_phone_number)}
                  </span>
                </>
              ) : (
                <>
                  <span className="font-semibold text-brand-text-primary">{fullName}</span>
                  <span className="text-muted-foreground">·</span>
                  <span className="text-muted-foreground truncate">{localData.customer_email}</span>
                  <span className="text-muted-foreground">·</span>
                  <span className="text-muted-foreground">
                    {formatPhoneNumberIntl(localData.customer_phone_number)}
                  </span>
                </>
              )}
            </div>
            <button
              type="button"
              onClick={() => setIsEditing(true)}
              className="text-sm font-semibold text-brand-primary hover:underline shrink-0 h-fit"
            >
              {tOrderInfo('buttons.edit')}
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-2">
            <FirstNameField
              value={localData.customer_first_name}
              onChange={(value: string) => update('customer_first_name', value)}
              validationErrors={flattenValidationErrors('customer_first_name', validationErrors, t)}
              className="lg:col-span-1 col-span-2"
            />
            <LastNameField
              value={localData.customer_last_name}
              onChange={(value: string) => update('customer_last_name', value)}
              validationErrors={flattenValidationErrors('customer_last_name', validationErrors, t)}
              className="lg:col-span-1 col-span-2"
            />
            <EmailField
              value={localData.customer_email}
              onChange={(value: string) => update('customer_email', value)}
              validationErrors={flattenValidationErrors('customer_email', validationErrors, t)}
              className="col-span-2"
            />
            <PhoneNumberInput
              value={localData.customer_phone_number}
              placeholder={t('form.customer_phone_number.placeholder')}
              className="col-span-2"
              onChange={(value) => update('customer_phone_number', value)}
              validationErrors={flattenValidationErrors('customer_phone_number', validationErrors, t)}
              initialCountry={initialData.company?.country}
            />
          </div>
        )}
      </div>
    </Section>
  );
}
